// Reports.jsx — Yearly monthly summary (Invoices + Credit Notes)
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import dayjs from 'dayjs';

const INR = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Reports() {
  const curYear = new Date().getFullYear();
  const [year, setYear] = useState(curYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);          // { msg, type }
  const [busy, setBusy] = useState(null);            // { type: 'invoices'|'credit', ym: 'YYYY-MM' }
  const [error, setError] = useState('');

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };
  const prettyYM = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return `${MONTHS[(m || 1) - 1]} ${y}`;
  };

  const years = useMemo(() => {
    const arr = [];
    for (let y = curYear + 0; y >= curYear - 3; y--) arr.push(y);
    return arr;
  }, [curYear]);

  async function load(y) {
    setLoading(true); setError('');
    try {
      const resp = await api.get('/reports/summary', { params: { year: y } });
      const data = Array.isArray(resp.data) ? resp.data : [];
      const filled = Array.from({ length: 12 }, (_, i) => {
        const found = data.find((d) => Number(d.month) === i + 1) || {};
        return {
          month: i + 1,
          invoice_total: Number(found.invoice_total || 0),
          credit_total: Number(found.credit_total || 0),
        };
      });
      setRows(filled);
    } catch (e) {
      console.error(e);
      setError('Failed to load report summary');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  const downloadInvoicesZip = async (year, month) => {
    const ym = `${year}-${String(month).padStart(2,'0')}`;
    setBusy({ type: 'invoices', ym });
    try {
      const res = await api.get(`/reports/exports/${ym}/invoices.zip`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `invoices-${ym}.zip`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) showToast('No invoices found for this month.');
      else showToast('Invoice ZIP download failed.', 'error');
    } finally { setBusy(null); }
  };

  const downloadCreditNotesZip = async (year, month) => {
    const ym = `${year}-${String(month).padStart(2,'0')}`;
    setBusy({ type: 'credit', ym });
    try {
      const res = await api.get(`/reports/exports/${ym}/credit-notes.zip`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `credit-notes-${ym}.zip`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) showToast('No credit notes found for this month.');
      else showToast('Credit notes ZIP download failed.', 'error');
    } finally { setBusy(null); }
  };

  useEffect(() => { load(year); }, [year]);

  const invoiceYearSum = rows.reduce((s, r) => s + (r.invoice_total || 0), 0);
  const creditYearSum  = rows.reduce((s, r) => s + (r.credit_total || 0), 0);
  const monthsWithInvoices = rows.filter((r) => r.invoice_total > 0).length;
  const monthsWithCredits  = rows.filter((r) => r.credit_total > 0).length;

  return (
    <div className="rpt-page light">
      {/* Header / Toolbar */}
      <header className="rpt-header">
        <div className="rpt-title">
          <h2>Reports</h2>
          <p className="rpt-sub">Yearly monthly summary (Invoices & Credit Notes)</p>
        </div>
        <div className="rpt-yearpicker">
          <label htmlFor="year-dd">Year</label>
          <select id="year-dd" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </header>

      {/* Summary cards */}
      <section className="rpt-cards">
        <div className="rpt-card">
          <div className="rpt-card-top">
            <span className="rpt-kicker">Invoices YTD</span>
            <span className="rpt-chip">{monthsWithInvoices} month</span>
          </div>
          <div className="rpt-amount">{INR(invoiceYearSum)}</div>
        </div>
        <div className="rpt-card">
          <div className="rpt-card-top">
            <span className="rpt-kicker">Credit Notes YTD</span>
            <span className="rpt-chip alt">{monthsWithCredits} month</span>
          </div>
          <div className="rpt-amount">{INR(creditYearSum)}</div>
        </div>
      </section>

      {/* Table */}
      <div className="rpt-tablewrap">
        <table className="rpt-table">
          <colgroup>
            <col className="col-month" style={{ width: '28%' }} />
            <col className="col-inv"   style={{ width: '24%' }} />
            <col className="col-cr"    style={{ width: '24%' }} />
            <col className="col-dl"    style={{ width: '24%' }} />
          </colgroup>

          <thead>
            <tr>
              <th>Month</th>
              <th className="ta-right">Invoice Total</th>
              <th className="ta-right">Credit Note Total</th>
              <th className="ta-right">Download</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="rpt-loading">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={4} className="rpt-empty">No data</td></tr>
            )}
            {!loading &&
              rows.map((r) => {
                const ym = `${year}-${String(r.month).padStart(2, '0')}`;
                const zeroInv = r.invoice_total <= 0;
                const zeroCr = r.credit_total <= 0;
                return (
                  <tr key={r.month}>
                    <td>
                      <div className="rpt-month">
                        <div className="name">{MONTHS[r.month - 1]}</div>
                        <div className="sub">
                          {dayjs(`${year}-${String(r.month).padStart(2, '0')}-01`).format('MMM YYYY')}
                        </div>
                      </div>
                    </td>
                    {/* FIXED: apply both classes correctly */}
                    <td className={`ta-right ${zeroInv ? 'muted' : ''}`}>{INR(r.invoice_total)}</td>
                    <td className={`ta-right ${zeroCr  ? 'muted' : ''}`}>{INR(r.credit_total)}</td>
                    <td className="ta-right">
                      <div className="rpt-actions">
                        {/* Always clickable; only disabled while busy */}
                        <button
                          className={`btn primary ${zeroInv ? 'subtle' : ''}`}
                          aria-label={`Download invoices ZIP for ${ym}`}
                          onClick={() => downloadInvoicesZip(year, r.month)}
                          disabled={!!busy}
                        >
                          {busy?.type === 'invoices' && busy?.ym === ym ? 'Preparing…' : 'Invoices ZIP'}
                        </button>
                        <button
                          className={`btn primary ${zeroCr ? 'subtle' : ''}`}
                          aria-label={`Download credit notes ZIP for ${ym}`}
                          onClick={() => downloadCreditNotesZip(year, r.month)}
                          disabled={!!busy}
                        >
                          {busy?.type === 'credit' && busy?.ym === ym ? 'Preparing…' : 'Credit Notes ZIP'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              <th className="ta-right">{INR(invoiceYearSum)}</th>
              <th className="ta-right">{INR(creditYearSum)}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Preparing overlay */}
      {busy && (
        <div className="rpt-overlay">
          <div className="rpt-modal">
            <div className="spinner" />
            <div className="title">
              Preparing {busy?.type === 'invoices' ? 'Invoices' : 'Credit Notes'} ZIP
            </div>
            <div className="sub">{prettyYM(busy?.ym || '')} • Please wait…</div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`rpt-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>
      )}

      {/* Styles (scoped) */}
      <style>{`
        :root { --rpt-bg:#f8fafc; --rpt-card:#ffffff; --rpt-muted:#6b7280; --rpt-line:#e5e7eb; --rpt-accent:#2563eb; --rpt-accent-2:#059669; --rpt-text:#0f172a; }
        .rpt-page { background: var(--rpt-bg); color: var(--rpt-text); padding: 16px; max-width: 1100px; margin: 0 auto; }
        .rpt-header { display:flex; gap:16px; align-items:center; justify-content:space-between; margin-bottom: 16px; }
        .rpt-title h2 { margin:0; font-size: 22px; letter-spacing:.2px; }
        .rpt-sub { margin:4px 0 0 0; color: var(--rpt-muted); font-size: 13px; }
        .rpt-yearpicker { display:flex; gap:8px; align-items:center; }
        .rpt-yearpicker label { color: var(--rpt-muted); font-size: 13px; }
        .rpt-yearpicker select { background:#ffffff; color:var(--rpt-text); border:1px solid var(--rpt-line); border-radius:10px; padding:8px 10px; min-width:120px; }

        .rpt-cards { display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap:12px; margin-bottom: 14px; }
        .rpt-card { background: var(--rpt-card); border:1px solid var(--rpt-line); border-radius: 16px; padding: 14px; box-shadow: 0 8px 18px rgba(15,23,42,.05); }
        .rpt-card-top { display:flex; justify-content:space-between; align-items:center; margin-bottom: 8px; }
        .rpt-kicker { color: var(--rpt-muted); font-size:12px; text-transform: uppercase; letter-spacing:.4px; }
        .rpt-chip { font-size:11px; color:#1e3a8a; background:#e0e7ff; border:1px solid #93c5fd; padding: 2px 8px; border-radius:999px; }
        .rpt-chip.alt { color:#065f46; background:#d1fae5; border-color:#34d399; }
        .rpt-amount { font-size: 22px; font-weight: 700; }

        .rpt-tablewrap { background: var(--rpt-card); border:1px solid var(--rpt-line); border-radius: 16px; overflow:hidden; }
        .rpt-table { width:100%; border-collapse: collapse; table-layout: fixed; }
        .rpt-table thead th { position: sticky; top:0; background:#f1f5f9; color:#334155; text-align:left; font-weight:600; font-size:12px; text-transform:uppercase; letter-spacing:.3px; padding:12px 16px; border-bottom:1px solid var(--rpt-line); }
        .rpt-table td, .rpt-table th { vertical-align: middle; }
        .rpt-table tbody td, .rpt-table tfoot th { padding: 12px 16px; border-bottom:1px solid var(--rpt-line); }
        .rpt-table tbody tr:hover { background: rgba(37,99,235,0.06); }

        .ta-right { text-align: right; font-variant-numeric: tabular-nums; font-feature-settings: 'tnum' 1, 'lnum' 1; }
        .rpt-loading, .rpt-empty { text-align:center; color: var(--rpt-muted); padding: 18px; }
        .rpt-month .name { font-weight:600; }
        .rpt-month .sub { color: var(--rpt-muted); font-size: 12px; }
        .muted { color: var(--rpt-muted); }

        .rpt-actions { display:flex; gap:8px; justify-content:flex-end; align-items:center; min-width:0; }
        .btn { border-radius:10px; padding:8px 12px; border:1px solid var(--rpt-line); background:#ffffff; color:var(--rpt-text); font-weight:600; font-size:13px; cursor:pointer; transition: transform .06s ease, background .2s ease, opacity .2s; }
        .btn:hover { transform: translateY(-1px); }
        .btn:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .btn.primary { background: var(--rpt-accent); color:#fff; border-color: var(--rpt-accent); }
        .btn.outline { background:#fff; border-color: var(--rpt-accent); color: var(--rpt-accent); }
        .btn.subtle { opacity:.9; }

        .rpt-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.25); display:flex; align-items:center; justify-content:center; z-index: 9998; }
        .rpt-modal { background:#ffffff; border:1px solid var(--rpt-line); color:#111827; padding: 18px 20px; border-radius: 14px; min-width: 300px; text-align:center; box-shadow: 0 12px 30px rgba(0,0,0,.18); }
        .spinner { width:28px; height:28px; border:3px solid #111827; border-bottom-color: transparent; border-radius: 50%; margin: 0 auto 12px; animation: spin 1s linear infinite; }
        .title { font-weight:700; margin-bottom: 4px; }
        .sub { opacity:.85; font-size:12px; }

        .rpt-toast { position: fixed; right: 16px; bottom: 16px; z-index: 9999; background:#111827; color:#fff; padding:10px 14px; border-radius:10px; border:1px solid #0b1220; box-shadow: 0 6px 20px rgba(0,0,0,.25); }
        .rpt-toast.error { background:#b91c1c; border-color:#7f1d1d; }

        @keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }

        /* ------- HARD mobile overrides ------- */
        @media (max-width: 560px) {
          /* Let content drive widths & override inline <col> widths */
          .rpt-table { table-layout: auto; }
          .rpt-table colgroup col:nth-child(1) { width: 44% !important; }
          .rpt-table colgroup col:nth-child(2) { width: 18% !important; }
          .rpt-table colgroup col:nth-child(3) { width: 18% !important; }
          .rpt-table colgroup col:nth-child(4) { width: 20% !important; }

          /* Stack action buttons and allow them to fill the cell */
          .rpt-actions { display: grid !important; grid-template-columns: 1fr !important; gap: 8px; justify-items: stretch; }
          .rpt-actions .btn { width: 100% !important; min-width: 0 !important; padding: 8px 10px !important; font-size: 12px !important; }

          /* Slightly tighter cells to buy space */
          .rpt-table thead th { padding:10px 12px; font-size:11px; }
          .rpt-table tbody td, .rpt-table tfoot th { padding:10px 12px; }
          .rpt-table tbody td.ta-right, .rpt-table tfoot th.ta-right { font-size: 13px; }
        }

        @media (max-width: 380px) {
          .rpt-actions .btn { padding: 7px 8px !important; font-size: 11px !important; }
        }

        /* Safety: never allow horizontal scroll from this page */
        html, body, #root { max-width: 100%; overflow-x: hidden; }
      `}</style>
    </div>
  );
}
